<template>
  <ImpersonateBanner
    v-if="showImpersonateBanner"
    v-test="'impersonate-banner'"
  />
  <div :class="$style.base">
    <TheToolTip />
    <TheNotification />
    <router-view v-if="dataLoaded" />
  </div>
</template>

<script lang="ts">
import { useFavicon } from '@vueuse/core';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import config from '@/config';
import { updateDataLayer } from '@/helpers/gtm';
import {
  NativeAppMessage,
  sendNativeAppMessage,
  useNativeAppMessage,
} from '@/helpers/native-app-bridge';
import { useLocalizationStore } from '@/stores/localization';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useSessionStore } from '@/stores/session';
import { useThemeStore } from '@/stores/theme';
import { useUserStore } from '@/stores/user';
import { device } from '@/user-context';
import ImpersonateBanner from './ImpersonateBanner.vue';
import TheNotification from './TheNotifications.vue';
import TheToolTip from './TheTooltip.vue';

export default defineComponent({
  name: 'App',
  components: {
    TheToolTip,
    TheNotification,
    ImpersonateBanner,
  },
  setup() {
    const { appendMessageHandlerToWindow } = useNativeAppMessage();

    const icon = useFavicon();

    watchEffect(() => {
      if (config.isTreatwellDomain) {
        icon.value = '/img/brand/treatwell-expert-favicon.ico';
        document.title = document.title.replace('- Salonized', '- Treatwell');
      }
    });

    useThemeStore();
    useLocalizationStore();

    return {
      appendMessageHandlerToWindow,
    };
  },
  watch: {
    dataLoaded(value) {
      if (value) {
        const preloaderEl = document.querySelector('.js-preloader');
        if (preloaderEl) {
          preloaderEl.remove();
        }
        const { user } = useUserStore();

        this.appendMessageHandlerToWindow();

        sendNativeAppMessage({
          message: NativeAppMessage.AppLoaded,
          payload: {
            hasPushNotificationEnabled:
              !!user?.settings.pushNotifications.newAppointmentInDays,
            isUserAuthenticated: !!user,
          },
        });
      }
    },
  },
  computed: {
    dataLoaded() {
      const { requiredDataLoaded } = useSessionStore();
      return requiredDataLoaded;
    },
    ...mapState(useUserStore, ['user']),
    showImpersonateBanner() {
      const { screenSize } = usePageLayoutStore();
      return (
        screenSize !== 's' &&
        this.user?.salonizedAdmin &&
        this.user?.impersonated
      );
    },
  },
  methods: {
    setupDom() {
      const { setScreenSize } = usePageLayoutStore();

      window.addEventListener('resize', () => {
        setScreenSize();
      });

      setScreenSize();

      if (device.touch) {
        document.body.classList.add('is-touch');
      }
    },
  },
  created() {
    updateDataLayer();
    this.setupDom();
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
}

.preloader {
  &:global(.v-leave-active) {
    transition: opacity 0.1s;
  }
  &:global(.v-leave-to) {
    opacity: 0;
  }
}
</style>
